import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { 
  FaBed, FaUsers, FaClock, FaUtensils, FaWater, 
  FaShip, FaHiking, FaFish, FaWifi, FaParking,
  FaVideo, FaBolt, FaTree, FaHome, FaUmbrellaBeach,
  FaChild, FaBirthdayCake, FaFire, FaHandshake, FaMountain,
  FaSwimmingPool, FaSpa, FaCocktail
} from 'react-icons/fa';

// Styled Components
const FacilitiesSection = styled.section`
  padding: 5rem 0;
  background-color: #f8fafc;
`;

const SectionTitle = styled.h2`
  text-align: center;
  font-size: 2.5rem;
  margin-bottom: 3rem;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 3px;
    background-color: #f59e0b;
  }
`;

const SectionSubtitle = styled.p`
  text-align: center;
  font-size: 1.25rem;
  margin-bottom: 2rem;
  color: #4b5563;
`;

const FacilitiesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 2rem;
`;

const FacilityCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: white;
  border-radius: 8px;
  padding: 1.25rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 15px rgba(0, 0, 0, 0.1);
  }
`;

const IconContainer = styled.div`
  font-size: 1.75rem;
  color: #1e3a8a;
  margin-bottom: 1rem;
  min-width: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FacilityTitle = styled.p`
  margin: 0;
  font-size: 1rem;
  color: #4b5563;
  text-align: center;
`;

// Icon mapping for dynamic rendering
const iconComponents = {
  FaWifi: <FaWifi />,
  FaParking: <FaParking />,
  FaUtensils: <FaUtensils />,
  FaSwimmingPool: <FaSwimmingPool />,
  FaSpa: <FaSpa />,
  FaCocktail: <FaCocktail />,
  FaBed: <FaBed />,
  FaUsers: <FaUsers />,
  FaClock: <FaClock />,
  FaWater: <FaWater />,
  FaShip: <FaShip />,
  FaHiking: <FaHiking />,
  FaFish: <FaFish />,
  FaVideo: <FaVideo />,
  FaBolt: <FaBolt />,
  FaTree: <FaTree />,
  FaHome: <FaHome />,
  FaUmbrellaBeach: <FaUmbrellaBeach />,
  FaChild: <FaChild />,
  FaBirthdayCake: <FaBirthdayCake />,
  FaFire: <FaFire />,
  FaHandshake: <FaHandshake />,
  FaMountain: <FaMountain />
};

// Default facilities data in case Firebase fetch fails
const defaultFacilities = [
  {
    id: 1,
    iconName: 'FaWifi',
    text: 'Free WiFi'
  },
  {
    id: 2,
    iconName: 'FaParking',
    text: 'Free Parking'
  },
  {
    id: 3,
    iconName: 'FaUtensils',
    text: 'Restaurant'
  },
  {
    id: 4,
    iconName: 'FaSwimmingPool',
    text: 'Swimming Pool'
  },
  {
    id: 5,
    iconName: 'FaSpa',
    text: 'Spa Services'
  },
  {
    id: 6,
    iconName: 'FaCocktail',
    text: 'Bar & Lounge'
  },
  {
    id: 7,
    iconName: 'FaBed',
    text: 'Deluxe rooms with lake view'
  },
  {
    id: 8,
    iconName: 'FaUsers',
    text: 'Family setup'
  },
  {
    id: 9,
    iconName: 'FaClock',
    text: '24/7 room service'
  },
  {
    id: 10,
    iconName: 'FaWater',
    text: 'Lakefront dining area'
  },
  {
    id: 11,
    iconName: 'FaShip',
    text: 'Shikara ride'
  },
  {
    id: 12,
    iconName: 'FaHiking',
    text: 'Trekking'
  },
  {
    id: 13,
    iconName: 'FaFish',
    text: 'Fishing'
  },
  {
    id: 14,
    iconName: 'FaVideo',
    text: 'CCTV security'
  },
  {
    id: 15,
    iconName: 'FaBolt',
    text: 'Backup generator'
  },
  {
    id: 16,
    iconName: 'FaTree',
    text: 'Family lawn area'
  },
  {
    id: 17,
    iconName: 'FaHome',
    text: 'Family quarters'
  },
  {
    id: 18,
    iconName: 'FaUmbrellaBeach',
    text: 'Picnic spot'
  },
  {
    id: 19,
    iconName: 'FaChild',
    text: 'Kids play area'
  },
  {
    id: 20,
    iconName: 'FaBirthdayCake',
    text: 'Birthday, marriage, ring ceremony setup'
  },
  {
    id: 21,
    iconName: 'FaFire',
    text: 'Bonfire'
  },
  {
    id: 22,
    iconName: 'FaHandshake',
    text: 'Conference, meeting setup'
  },
  {
    id: 23,
    iconName: 'FaMountain',
    text: 'Wular lake viewpoint'
  }
];

const Facilities = () => {
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
   
    const fetchFacilities = async () => {
      try {
        setLoading(true);
        const facilitiesCollection = collection(db, 'facilities');
        const snapshot = await getDocs(facilitiesCollection);
        
        if (!snapshot.empty) {
          const facilitiesData = [];
          snapshot.forEach((doc) => {
            facilitiesData.push({ id: doc.id, ...doc.data() });
          });
          setFacilities(facilitiesData);
        } else {
          // Use default facilities if none found in Firebase
          setFacilities(defaultFacilities);
        }
      } catch (error) {
        console.error('Error fetching facilities:', error);
        // Fallback to default facilities on error
        setFacilities(defaultFacilities);
      } finally {
        setLoading(false);
      }
    };
    
    fetchFacilities();
  }, []);

  if (loading) {
    return (
      <FacilitiesSection id="facilities">
        <SectionTitle>Our Facilities</SectionTitle>
        <SectionSubtitle>Loading facilities...</SectionSubtitle>
      </FacilitiesSection>
    );
  }

  return (
    <FacilitiesSection id="facilities">
      <SectionTitle>Our Facilities</SectionTitle>
      <SectionSubtitle>Enjoy premium amenities during your stay</SectionSubtitle>
      
      <FacilitiesGrid>
        {facilities.map((facility) => (
          <FacilityCard key={facility.id}>
            <IconContainer>
              {iconComponents[facility.iconName] || <FaWifi />}
            </IconContainer>
            <FacilityTitle>{facility.text}</FacilityTitle>
          </FacilityCard>
        ))}
      </FacilitiesGrid>
    </FacilitiesSection>
  );
};

export default Facilities;
